import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import ec from "@/locales/ec.json";
import en from "@/locales/en.json";

const DICTS = { ec, en };
export const SUPPORTED_LANGS = ["ec", "en"];
export const DEFAULT_LANG = "ec";

const I18nContext = createContext(null);

function getFromPath(obj, path) {
  const parts = path.split(".");
  let current = obj;
  for (const key of parts) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

export function I18nProvider({ lang, children }) {
  const activeLang = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
  const dict = DICTS[activeLang];

  const t = useCallback(
    (key, fallback = "") => {
      const value = getFromPath(dict, key);
      if (value === undefined || value === null) {
        const fromDefault = getFromPath(DICTS[DEFAULT_LANG], key);
        return fromDefault ?? fallback ?? key;
      }
      return value;
    },
    [dict]
  );

  const value = useMemo(
    () => ({ t, lang: activeLang, dict }),
    [t, activeLang, dict]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
}

/**
 * Language route wrapper reads the :lang param and renders the provider.
 */
export function LangProvider({ children }) {
  const { lang } = useParams();
  const active = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
  return <I18nProvider lang={active}>{children}</I18nProvider>;
}
